import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import {
  ListadoActualizando, ListadoCargando, ListadoConError,
} from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { consultarTablero } from '../api/presupuestoApi.js';
import { EstadoDeMonitoreo } from '../componentes/EstadoDeMonitoreo.jsx';
import { SelectorDeAnio } from '../componentes/SelectorDeAnio.jsx';
import { ResumenDeCuatroRenglones } from '../componentes/ResumenDeCuatroRenglones.jsx';
import {
  anioDePeriodo, avance, esNegativo, etiquetaDePeriodo, importe, importeEnDivisa,
  mesAnterior, mesSiguiente, nombreDePeriodo, periodoDeFechaLocal,
} from '../formato.js';

/**
 * TABLERO DE MONITOREO DEL PRESUPUESTO (RF-101, RF-102, RF-103, RF-105).
 *
 * UNA SOLA LLAMADA resuelve el mes completo (RNF-047) y todo llega resuelto: la
 * resta de tres términos, el orden alfabético de las dos listas, los estados y
 * las advertencias. Esta pantalla NO suma, NO resta, NO reordena y NO deduce
 * ningún estado (RNF-053, RN-225, RN-228).
 *
 * LO ANUAL QUE OFRECE ES UNA ACCIÓN VISIBLE, nunca un diálogo que salte solo
 * (RN-223c): «Definir el presupuesto de AAAA» hay que pulsarlo a propósito. El
 * tablero no muestra ni pide el porcentaje anual (D-152).
 */
export default function TableroDePresupuesto() {
  const hoy = obtenerFechaDeNegocioHoy();
  const [idPeriodo, setIdPeriodo] = useState(() => periodoDeFechaLocal(hoy));

  const consultar = useCallback(
    () => consultarTablero({ idPeriodo, fechaLocal: hoy }),
    [idPeriodo, hoy],
  );

  // Un mes SIN líneas y SIN gasto no es un error: es un mes en blanco (RN-214).
  const listado = useConsultaDeListado(
    consultar,
    (respuesta) => (respuesta?.lineas ?? []).length === 0
      && (respuesta?.gastoSinPresupuesto ?? []).length === 0,
  );

  const tablero = listado.datos;
  const anio = anioDePeriodo(idPeriodo);
  const esMesEnCurso = idPeriodo === periodoDeFechaLocal(hoy);

  /*
   * EL AÑO DE LA ACCIÓN ANUAL (RF-275, incremento 22/22). Viene PROPUESTO con el
   * año del mes que se está viendo —que es lo que esta pantalla hacía siempre— y se
   * puede cambiar. En cuanto la persona elige uno, SU AÑO MANDA y moverse de mes ya
   * no se lo pisa: es el mismo criterio de D-406, «que la app lo proponga y yo
   * corrijo».
   */
  const anioEnCurso = anioDePeriodo(periodoDeFechaLocal(hoy));
  const [anioElegido, setAnioElegido] = useState(anio);
  const eligioElAnio = useRef(false);

  useEffect(() => {
    if (!eligioElAnio.current) setAnioElegido(anio);
  }, [anio]);

  function elegirAnio(elegido) {
    eligioElAnio.current = true;
    setAnioElegido(Number(elegido));
  }

  return (
    <Shell titulo="Presupuesto" subtitulo="Tablero de monitoreo">
      <ListadoActualizando visible={listado.actualizando} />

      <div className="card" style={{ margin: 12 }} data-prueba="selector-de-mes">
        <div className="fila">
          <button className="btn sec" type="button" onClick={() => setIdPeriodo(mesAnterior(idPeriodo))}>
            Mes anterior
          </button>
          <span className="crece" style={{ textAlign: 'center' }}>
            <b data-prueba="mes-en-pantalla">{etiquetaDePeriodo(idPeriodo)}</b>
            <small style={{ display: 'block' }}>Periodo {idPeriodo} · mes calendario natural, sin cierre de mes</small>
            {esMesEnCurso && <span className="chip">Mes en curso</span>}
            {tablero?.esPeriodoFuturo && <span className="chip mo" data-prueba="marca-proyeccion">Proyección</span>}
          </span>
          <button className="btn sec" type="button" onClick={() => setIdPeriodo(mesSiguiente(idPeriodo))}>
            Mes siguiente
          </button>
        </div>
      </div>

      {listado.estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={listado.error} alReintentar={listado.recargar} />
      )}

      {listado.estado === ESTADO_DE_LISTADO.CARGANDO && <ListadoCargando texto="Consultando el presupuesto del mes…" />}

      {/* Texto ya armado por el servidor (RNF-053): aquí viaja, por ejemplo, "no se
          pudo repartir las compras a meses" cuando el 4/6 no respondió (RN-212). */}
      {(tablero?.advertencias ?? []).map((aviso) => (
        <div className="aviso w" role="status" key={aviso} data-prueba="advertencia-del-servidor">⚠ {aviso}</div>
      ))}

      {tablero && listado.estado === ESTADO_DE_LISTADO.VACIO && (
        <div className="card" style={{ margin: 12 }} data-prueba="mes-vacio">
          <b>Aún no defines presupuesto para este mes</b>
          <span className="ayuda">
            Tampoco hubo gasto registrado en {nombreDePeriodo(idPeriodo)}. Un mes sin datos no es un
            error: está en blanco y listo para capturarse.
          </span>
          <Link className="btn ancho" to={`/presupuesto/registro/${idPeriodo}`}>
            Capturar el presupuesto de {etiquetaDePeriodo(idPeriodo)}
          </Link>
        </div>
      )}

      {tablero && listado.estado !== ESTADO_DE_LISTADO.VACIO && (
        <>
          <ResumenDeCuatroRenglones tablero={tablero} />

          <div className="card" style={{ margin: 12 }} data-prueba="avance-global">
            <div className="fila">
              <span className="crece">
                Avance global
                <small style={{ display: 'block' }}>solo con lo presupuestado y su gasto</small>
              </span>
              <b>{avance(tablero.porcentajeAvanceGlobal)}</b>
            </div>
            <EstadoDeMonitoreo
              claveEstado={tablero.claveEstadoGlobal}
              esProyeccion={tablero.esPeriodoFuturo}
              marca="estado-global"
            />
          </div>
        </>
      )}

      {/* ACCIÓN VISIBLE DEL AÑO (RF-110). Nada se genera al abrir la aplicación ni
          al cambiar de mes: esto se pulsa a propósito (RN-223c). */}
      <div className="card" style={{ margin: 12 }} data-prueba="accion-anual">
        <b>Definir el presupuesto de {anioElegido}</b>
        <span className="ayuda">
          Un porcentaje —o mantener el que ya está fijado— y los <b>doce meses</b> quedan definidos
          de una vez, sin confirmar categoría por categoría. Después puedes entrar a cualquier mes
          y editarlo. Aquí también se cambia el porcentaje del año.
        </span>

        {/* DE QUÉ AÑO (RF-275, incremento 22/22). Antes esta acción solo podía ser
            del año del mes que se estaba viendo; con el calendario extendido se
            puede presupuestar cualquier año completo, así que el año se elige.

            `aniosConPresupuesto` es el dato ADITIVO del tablero (contratos-topes
            rev. 2) que alimenta la marca de visto: llega resuelto y ordenado del
            servidor y aquí sólo se pasa (RNF-053). Si no llegó —servidor viejo,
            tablero aún cargando—, el selector se pinta sin marcas y nada se rompe
            (RN-773). */}
        <SelectorDeAnio
          anioEnCurso={anioEnCurso}
          anioElegido={anioElegido}
          alElegir={elegirAnio}
          aniosConPresupuesto={tablero?.aniosConPresupuesto ?? []}
          datoDePrueba="accion-anual-anio"
        />

        <Link className="btn ancho" to={`/presupuesto/anio/${anioElegido}?desde=${idPeriodo}`}>
          Definir el presupuesto de {anioElegido}
        </Link>
      </div>

      {tablero && listado.estado !== ESTADO_DE_LISTADO.VACIO && (
        <>
          <div className="card lista" style={{ margin: 12 }} data-prueba="lineas-con-presupuesto">
            <div className="gtit">1 · Categorías con presupuesto</div>
            <span className="ayuda">
              Orden <b>alfabético resuelto por el servidor</b>: la pantalla no reordena ni agrupa.
            </span>

            {tablero.lineas.length === 0 && (
              <div className="aviso i" data-prueba="sin-lineas">
                Ninguna categoría tiene presupuesto en {nombreDePeriodo(idPeriodo)}. Cuando definas
                alguna, aparecerá aquí con su estado.
              </div>
            )}

            {tablero.lineas.map((linea) => (
              <div
                className="fila"
                key={linea.idPresupuestoDeCategoria}
                data-prueba="linea-de-presupuesto"
                data-categoria={linea.idCategoria}
                style={linea.categoriaActiva ? undefined : { opacity: 0.72 }}
              >
                <div className="crece">
                  <b>{linea.nombreCategoria}</b>
                  <div>
                    <EstadoDeMonitoreo
                      claveEstado={linea.claveEstado}
                      nombreEstado={linea.nombreEstado}
                      iconoEstado={linea.iconoEstado}
                      claveTokenDeColor={linea.claveTokenDeColor}
                      esProyeccion={tablero.esPeriodoFuturo}
                      marca="estado-de-linea"
                    />
                  </div>

                  <small>
                    Presupuesto {importe(linea.importeAsignado)} · gastado {importe(linea.gastado)} ·
                    avance {avance(linea.porcentajeAvance)}
                  </small>

                  {/* Informativo: YA ESTÁ INCLUIDO en el gastado, no se suma otra vez (RN-197b). */}
                  {linea.importeImputadoDeMeses && !/^0*\.?0*$/.test(String(linea.importeImputadoDeMeses)) && (
                    <small data-prueba="imputado-de-meses">
                      Incluye {importe(linea.importeImputadoDeMeses)} de mensualidades de compras a
                      meses imputadas a este mes. Ya está dentro de lo gastado.
                    </small>
                  )}

                  {!linea.categoriaActiva && (
                    <small data-prueba="categoria-desactivada">
                      <b>Categoría desactivada.</b> La línea se conserva y se sigue monitoreando; su
                      importe se puede editar desde el registro, pero no se puede dar de alta otra.
                    </small>
                  )}
                </div>

                <span
                  className={`imp${esNegativo(linea.disponible) ? ' g' : ''}`}
                  data-prueba="disponible-de-linea"
                >
                  {importe(linea.disponible)}
                  <small>disponible</small>
                </span>
              </div>
            ))}
          </div>

          {/* BLOQUE 2 · gasto sin presupuesto: no suma en lo presupuestado, no se
              funde en el gastado y no marca excedido, pero SÍ resta en el
              disponible como tercer renglón (RF-103, RN-200, RN-228c). */}
          <div className="card lista" style={{ margin: 12 }} data-prueba="gasto-sin-presupuesto">
            <div className="gtit">2 · Gasto sin presupuesto</div>
            <span className="ayuda">
              Categorías con gasto en el mes y <b>sin</b> cifra presupuestada. Mismo orden
              alfabético del servidor.
            </span>

            {tablero.gastoSinPresupuesto.length === 0 ? (
              <span className="ayuda" data-prueba="sin-gasto-fuera-de-plan">
                En {nombreDePeriodo(idPeriodo)} no hay gasto en categorías sin presupuesto.
              </span>
            ) : tablero.gastoSinPresupuesto.map((renglon) => (
              <div className="fila" key={renglon.idCategoria} data-prueba="renglon-sin-presupuesto">
                <div className="crece">
                  <b>{renglon.nombreCategoria}</b>
                  <small>
                    {renglon.cantidadMovimientos} movimiento{renglon.cantidadMovimientos === 1 ? '' : 's'} en{' '}
                    {nombreDePeriodo(idPeriodo)}
                  </small>
                </div>
                <span className="imp">{importe(renglon.gastado)}</span>
                <Link className="btn sec" to={`/presupuesto/registro/${idPeriodo}?foco=${renglon.idCategoria}`}>
                  Definir presupuesto
                </Link>
              </div>
            ))}
          </div>

          {/* BLOQUE 3 · SOLO MXN (D-112, RN-193): el gasto en dólares se informa
              aparte, en su propia divisa y SIN CONVERTIR, para que no parezca
              inexistente. No entra en ninguno de los tres términos. */}
          <div className="card" style={{ margin: 12 }} data-prueba="gasto-en-otra-divisa">
            <div className="gtit">3 · Gasto en dólares, excluido del monitoreo</div>
            {tablero.gastoExcluidoEnOtraDivisa ? (
              <>
                <div className="fila">
                  <span className="crece">
                    Total del mes en cuentas en dólares
                    <small style={{ display: 'block' }}>
                      {tablero.gastoExcluidoEnOtraDivisa.cantidadMovimientos} movimientos · se muestra
                      en su propia divisa
                    </small>
                  </span>
                  <b className="imp" data-prueba="total-en-dolares">
                    {importeEnDivisa(
                      tablero.gastoExcluidoEnOtraDivisa.total,
                      tablero.gastoExcluidoEnOtraDivisa.codigoISO,
                    )}
                  </b>
                </div>
                <span className="ayuda">
                  El presupuesto es <b>solo en pesos</b>. Este gasto no consume presupuesto y{' '}
                  <b>no se convierte</b>: no hay tipo de cambio en el sistema.
                </span>
              </>
            ) : (
              <span className="ayuda">
                En {nombreDePeriodo(idPeriodo)} no hubo movimientos en cuentas en dólares. Cuando
                los haya, su total aparece aquí, en dólares y sin convertir.
              </span>
            )}
          </div>

          <div style={{ margin: 12 }}>
            <Link className="btn sec ancho" to={`/presupuesto/registro/${idPeriodo}`}>
              Registrar el presupuesto de {etiquetaDePeriodo(idPeriodo)}
            </Link>
          </div>
        </>
      )}
    </Shell>
  );
}
